import { Component, Injector, ElementRef, ViewChild } from '@angular/core';
import { Validators } from '@angular/forms';
import { takeUntil } from 'rxjs/operators';
import { MessageService } from 'primeng/api';
import { EmpenhoExtraService } from '../service/empenho-extra.service';
import { RetencaoExtraService } from '../service/retencao-extra.service';
import {
  BaseResourceFormComponent, EmpenhoExtra, LoginContabil,
  EddyAutoComplete, Favorecido, FichaExtra, Contrato, RetencaoExtra,
  GlobalService, FuncaoService, DateFormatPipe, Exercicio, FavorecidoService
} from 'eddydata-lib';
import { ContratoService } from 'administrativo-lib';
import { FichaExtraService } from '../../ficha-extra/service/ficha-extra.service';

@Component({
  selector: 'app-empenho-extra-form',
  templateUrl: './empenho-extra-form.component.html'
})
export class EmpenhoExtraFormComponent extends BaseResourceFormComponent<EmpenhoExtra, LoginContabil> {


  /**
   * Declaração de variáveis
   */
  @ViewChild('tipo_') inputField: ElementRef;

  public favorecidoAutoComplete: EddyAutoComplete<Favorecido>;
  public fichaAutoComplete: EddyAutoComplete<FichaExtra>;
  public contratoAutoComplete: EddyAutoComplete<Contrato>;
  public listaRetencoes: Array<RetencaoExtra>;

  public empenhado: number;
  public pago: number;

  /**
   * Construtor com as injeções de dependencias
   */
  constructor(
    protected injector: Injector,
    private messageService: MessageService,
    protected globalService: GlobalService,
    protected funcaoService: FuncaoService,
    protected favorecidoService: FavorecidoService,
    protected fichaService: FichaExtraService,
    protected contratoService: ContratoService,
    protected retencaoService: RetencaoExtraService,
    protected empenhoService: EmpenhoExtraService) {
    super(new EmpenhoExtra(), injector, EmpenhoExtra.converteJson, empenhoService);
  }

  // ========================================================================
  //                        MÉTODOS ABSTRAÍDOS
  // ========================================================================

  protected criarCamposForm(): void {
    this.entidadeForm = this.fb.group({
      id: [null],
      numero: [null, [Validators.required]],
      data_empenho: [null, [Validators.required]],
      data_vencimento: [null],
      valor_empenho: [0, [Validators.required]],
      mes: [null],
      especie: ['EME'],
      processo: [null, [Validators.required]],
      documento: [null],
      impresso: [0],
      aux: [0, [Validators.required]],
      historico: [null, [Validators.required]],
      orgao: [this.login.orgao, [Validators.required]],
      ficha: [null, [Validators.required]],
      favorecido: [null, [Validators.required]],
      contrato: [null],
      exercicio: [this.login.exercicio, [Validators.required]],
      usuario_cadastro: [this.login.usuario],
      retencoes: [null]
    });
  }

  protected parametrosExtras(): {} {
    return { relations: 'ficha,ficha.plano,ficha.recurso,ficha.aplicacao,favorecido,contrato,exercicio,orgao' };
  }

  protected afterInit(): void {
    this.carregarAutoCompletes();
    this.inicializaVariavel();
    if (this.currentActionRoute === 'novo') {
      this.empenhoService.proximoNumeroEmpenho(this.login.exercicio.id, this.login.orgao.id)
        .pipe(takeUntil(this.unsubscribe))
        .subscribe((res) => {
          this.entidadeForm.get('numero').setValue(res);
          this.empenhoService.ultimaDataEmpenhada(this.login.exercicio.id, this.login.orgao.id)
            .pipe(takeUntil(this.unsubscribe))
            .subscribe((dataEmp) => {
              this.entidade.data_empenho = new DateFormatPipe().transform(dataEmp, []);
              this.entidadeForm.get('data_empenho').setValue(this.entidade.data_empenho);
              this.entidadeForm.get('mes').setValue(this.entidade.data_empenho ? this.entidade.data_empenho.getUTCMonth() + 1 : 1);
            });
        });
    }
  }

  protected campoFoco(): ElementRef {
    return this.inputField;
  }

  private inicializaVariavel() {
    if (this.currentActionRoute === 'novo') {
      this.listaRetencoes = new Array<RetencaoExtra>();
    }
  }

  private async loadTotalizadores() {
    this.empenhado = 0;
    this.pago = 0;
    const exercicio: Exercicio = this.entidadeForm.get('exercicio').value
      ? this.entidadeForm.get('exercicio').value
      : this.login.exercicio;
    let totalizadores = await this.empenhoService.totalizadores(
      exercicio.id, this.login.orgao.id, this.entidade.id).toPromise();

    if (totalizadores) {
      totalizadores = totalizadores.content[0] ? totalizadores.content[0] : totalizadores.content;
      this.empenhado = totalizadores.total_empenhado;
      this.pago = totalizadores.total_pago;
    }    
  }

  protected async afterLoad() {
    this.entidade.data_empenho = new DateFormatPipe().transform(this.entidade.data_empenho, []);
    this.entidade.data_vencimento = new DateFormatPipe().transform(this.entidade.data_vencimento, []);
    await this.loadTotalizadores().then(() => {
      this.retencaoService.obterPorLiquidacao(this.entidade.id).pipe(takeUntil(this.unsubscribe))
        .subscribe(
          (data: any) => {
            this.listaRetencoes = data ? data.content : new Array<RetencaoExtra>();
          }, error => this.messageService.add({ severity: 'error', summary: 'Atenção', detail: error })
        );
    });

    this.favorecidoAutoComplete.id = this.entidade.favorecido ? this.entidade.favorecido.id : null;
    this.fichaAutoComplete.id = this.entidade.ficha ? this.entidade.ficha.numero : null;
    this.contratoAutoComplete.id = this.entidade.contrato ? this.entidade.contrato.id : null;
  }

  protected beforeSubmit() {
    try {
      if (!this.entidadeForm.get('numero').value) {
        throw new Error('Informe o número do empenho');
      }
      if (!this.entidadeForm.get('data_empenho').value) {
        throw new Error('Informe a data do empenho');
      }
      if (!this.entidadeForm.get('especie').value) {
        throw new Error('Informe a espécie do empenho');
      }
      if (!this.entidadeForm.get('processo').value) {
        throw new Error('Informe o processo do empenho');
      }
      if (!this.entidadeForm.get('valor_empenho').value) {
        throw new Error('Informe o valor do empenho');
      }
      if (!this.entidadeForm.get('exercicio').value) {
        throw new Error('Informe o exercício do empenho');
      }
      if (!this.entidadeForm.get('orgao').value) {
        throw new Error('Informe o órgão do empenho');
      }
      if (!this.entidadeForm.get('favorecido').value) {
        throw new Error('Informe o favorecido do empenho');
      }
      if (!this.entidadeForm.get('ficha').value) {
        throw new Error('Informe a ficha do empenho');
      }
      if (!this.entidadeForm.get('historico').value) {
        throw new Error('Informe o histórico do empenho');
      }
      if (this.entidadeForm.get('valor_empenho').value <= 0) {
        throw new Error('Informe o valor do empenho');
      }
      const dtEmpenho: Date = this.entidadeForm.get('data_empenho').value;
      if (dtEmpenho.getFullYear() !== this.login.exercicio.ano) {
        throw new Error('O ano da data do empenho está diferente do exercício logado');
      }
      this.entidadeForm.get('mes').setValue(dtEmpenho.getUTCMonth() + 1);
      this.entidadeForm.get('retencoes').setValue(this.listaRetencoes);
    } catch (e) {
      this.messageService.add({ severity: 'error', summary: 'Validação', detail: e });
      throw e;
    }
  }

  protected afterSubmit(ent: EmpenhoExtra) {
    this.listaRetencoes = new Array<RetencaoExtra>();
    if (this.currentActionRoute === 'novo') {
      this.router.navigate(['/empenhos-extra', 'novo']);
    } else if (this.currentActionRoute === 'editar') {
      this.router.navigate(['/empenhos-extra', ent.id, 'editar'], {skipLocationChange: true}).then(() => {
        this.afterLoad();
      });
    }
  }

  // ========================================================================
  //                            MÉTODOS DA CLASSE
  // ========================================================================

  private carregarAutoCompletes() {
    // autocomplete para favorecido
    this.favorecidoAutoComplete = new EddyAutoComplete(this.entidadeForm.get('favorecido'), this.favorecidoService,
      'id', ['nome'], { cidade_id: this.login.cidade.id, orderBy: 'nome' }, { number: ['id', 'cpf_cnpj'], text: ['nome'] }
    );

    // autocomplete para ficha
    this.fichaAutoComplete = new EddyAutoComplete(this.entidadeForm.get('ficha'), this.fichaService,
      'numero', ['numero', 'nome'],
      { exercicio_id: this.login.exercicio.id, orgao_id: this.login.orgao.id, relations: 'favorecido,plano,recurso,aplicacao', orderBy: 'nome' },
      { number: ['id', 'numero'], text: ['nome'] },
      () => {
        this.loadTotalizadores();
        this.entidadeForm.get('favorecido').setValue(this.entidadeForm.get('ficha').value.favorecido);
        this.favorecidoAutoComplete.id = this.entidadeForm.get('ficha').value.favorecido.id;
      }
    );

    // autocomplete para contrato
    this.contratoAutoComplete = new EddyAutoComplete(this.entidadeForm.get('contrato'), this.contratoService,
      'numero', ['numero'], { orgao_id: this.login.orgao.id, orderBy: 'numero' }, { number: ['id', 'numero'] }
    );
  }

  public voltar() {
    switch (this.login.sistema) {
      case 'contabil':
      case 'controle-interno':
        this.router.navigate(['/contabilidade']);
        break;
      default:
        this.sair();
        break;
    }
  }
}
